import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, PackageCheck, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Caption, Card, Empty, PageHeader, Spinner } from "@/components/Common";
import { Rich } from "@/components/Rich";
import { api, enc, errMsg, humanize, openApiPath } from "@/lib/api";

const isPrim = (v) => ["string", "number", "boolean"].includes(typeof v);

const Row = ({ r, onDeliver }) => {
  const cols = Object.entries(r).filter(([, v]) => isPrim(v) && String(v).length < 80).slice(0, 6);
  const deliverable = r.order_id && !["delivered", "completed"].includes(String(r.status).toLowerCase());
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-[#27272A] bg-[#09090B] p-3 md:flex-row md:items-center md:justify-between">
      <div className="grid flex-1 gap-x-6 gap-y-1 text-xs sm:grid-cols-3">
        {cols.map(([k, v]) => <div key={k} className="min-w-0"><span className="text-zinc-600">{humanize(k)}: </span><span className="break-all text-zinc-300">{String(v)}</span></div>)}
      </div>
      <div className="flex gap-2">
        {r.event_id && <Button size="sm" variant="outline" data-testid={`admin-resume-${r.event_id}`}
          onClick={() => openApiPath(() => api.post(`/admin/resume/${enc(r.event_id)}/link`)).catch((e) => toast.error(errMsg(e)))}><FileText className="h-3.5 w-3.5" />Resume</Button>}
        {deliverable && <Button size="sm" onClick={() => onDeliver(r.order_id)} data-testid={`admin-deliver-${r.order_id}`}><PackageCheck className="h-3.5 w-3.5" />Deliver</Button>}
      </div>
    </div>
  );
};

export default function Admin() {
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const load = () => api.get("/admin/overview").then((r) => setData(r.data)).catch((e) => { if ([401, 403].includes(e.response?.status)) setDenied(true); else toast.error(errMsg(e)); });
  useEffect(() => { load(); }, []);
  const deliver = async (id) => { try { await api.post(`/admin/orders/${enc(id)}/deliver`); toast.success("Marked delivered"); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (denied) return <Empty icon={ShieldCheck} title="Admins only" text="This desk is restricted to the RPOPRO operator account." />;
  if (!data) return <Spinner className="h-6 w-6 text-[#D4AF37]" />;
  const stats = Object.entries(data).filter(([, v]) => isPrim(v));
  const objs = Object.entries(data).filter(([, v]) => v && typeof v === "object" && !Array.isArray(v));
  const lists = Object.entries(data).filter(([, v]) => Array.isArray(v));
  return (
    <div>
      <PageHeader title="Admin Desk" subtitle="Operator overview: users, orders and resume events."><Link to="/admin/cc"><Button data-testid="admin-cc-link">Command Center</Button></Link><Link to="/admin/scans"><Button data-testid="admin-scans-link">Resume scan leads</Button></Link><Link to="/admin/wellbeing"><Button variant="outline" data-testid="admin-wellbeing-link">Well-being content</Button></Link></PageHeader>
      <div className="mb-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map(([k, v]) => <Card key={k}><Caption>{humanize(k)}</Caption><div className="mt-2 font-display text-3xl text-zinc-100">{String(v)}</div></Card>)}
      </div>
      {objs.map(([k, v]) => <Card key={k} className="mb-6"><Caption className="mb-3">{humanize(k)}</Caption><Rich value={v} /></Card>)}
      {lists.map(([k, rows]) => (
        <Card key={k} className="mb-6" data-testid={`admin-list-${k}`}>
          <Caption className="mb-3">{humanize(k)} · {rows.length}</Caption>
          <div className="space-y-2">{rows.length === 0 ? <p className="text-sm text-zinc-500">Empty</p> : rows.map((r, i) => isPrim(r) ? <p key={i} className="text-sm text-zinc-300">{String(r)}</p> : <Row key={r.order_id || r.event_id || i} r={r} onDeliver={deliver} />)}</div>
        </Card>
      ))}
    </div>
  );
}
